import { Navigate, createBrowserRouter } from 'react-router-dom';
import { RequireAuth, homePath, useMe } from './lib/auth';
import { NAV, BUILT } from './lib/nav';
import { AppShell, NoProgram } from './layouts/AppShell';
import { SignIn } from './pages/auth/SignIn';
import { MfaSetup, MfaVerify } from './pages/auth/Mfa';
import { AcceptInvite } from './pages/auth/AcceptInvite';
import { ResetPassword } from './pages/auth/ResetPassword';
import { GuidePage } from './guide/Guide';
import { TrackerPage } from './pages/tracker/TrackerPage';
import { PhasePage } from './pages/tracker/PhasePage';
import { MyWork } from './pages/MyWork';
import { DeliverablesPage } from './pages/deliverables/DeliverablesPage';
import { DeliverableDetail } from './pages/deliverables/DeliverableDetail';
import { DocumentsPage } from './pages/documents/DocumentsPage';
import { DocumentDetail } from './pages/documents/DocumentDetail';
import { ReportsPage } from './pages/reports/ReportsPage';
import { RaidPage } from './pages/raid/RaidPage';
import { ExecutivePage } from './pages/executive/ExecutivePage';
import { WorkRecordPage } from './pages/work-record/WorkRecordPage';
import { CriteriaPage } from './pages/criteria/CriteriaPage';
import { MeetingsPage } from './pages/meetings/MeetingsPage';
import { MeetingDetailPage } from './pages/meetings/MeetingDetail';
import { VendorsPage } from './pages/vendors/VendorsPage';
import { CategoryPage } from './pages/vendors/CategoryPage';
import { BudgetPage } from './pages/budget/BudgetPage';
import { ReportDetail } from './pages/reports/ReportDetail';
import { Placeholder } from './pages/Placeholder';
import { Account } from './pages/Account';
import { Admin } from './pages/admin/Admin';
import { Spinner } from './components/ui/misc';

function Home() {
  const { data: me, isLoading } = useMe();
  if (isLoading) return <Spinner className="h-full" />;
  if (!me) return <Navigate to="/signin" replace />;
  return <Navigate to={homePath(me)} replace />;
}

const built: Record<string, React.ReactElement> = {
  'my-work': <MyWork />,
  executive: <ExecutivePage />,
  tracker: <TrackerPage />,
  deliverables: <DeliverablesPage />,
  documents: <DocumentsPage />,
  raid: <RaidPage />,
  reports: <ReportsPage />,
  'work-record': <WorkRecordPage />,
  criteria: <CriteriaPage />,
  meetings: <MeetingsPage />,
  vendors: <VendorsPage />,
  budget: <BudgetPage />,
  admin: <Admin />,
  guide: <GuidePage />,
};

export const router = createBrowserRouter([
  { path: '/', element: <Home /> },
  { path: '/signin', element: <SignIn /> },
  { path: '/mfa', element: <MfaVerify /> },
  { path: '/mfa/setup', element: <MfaSetup /> },
  { path: '/invite/:token', element: <AcceptInvite /> },
  { path: '/reset/:token', element: <ResetPassword /> },
  {
    path: '/no-program',
    element: (
      <RequireAuth>
        <NoProgram />
      </RequireAuth>
    ),
  },
  {
    path: '/p/:programId',
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <Navigate to="tracker" replace /> },
      ...NAV.map((n) => ({
        path: n.path === 'admin' ? 'admin/*' : n.path,
        element: BUILT.has(n.path) ? built[n.path] : <Placeholder path={n.path} />,
      })),
      { path: 'tracker/phase/:phaseId', element: <PhasePage /> },
      { path: 'deliverables/:deliverableId', element: <DeliverableDetail /> },
      { path: 'documents/:documentId', element: <DocumentDetail /> },
      { path: 'reports/:reportId', element: <ReportDetail /> },
      { path: 'meetings/:meetingId', element: <MeetingDetailPage /> },
      { path: 'vendors/:categoryId', element: <CategoryPage /> },
      { path: 'account', element: <Account /> },
      { path: '*', element: <Navigate to="tracker" replace /> },
    ],
  },
  { path: '*', element: <Navigate to="/" replace /> },
]);
